import React, { useEffect, useState, useRef } from 'react';
import { XIcon, EyeIcon, EyeOffIcon, CheckIcon, EditIcon, CheckCircleIcon } from 'lucide-react';
interface AuthBottomSheetProps {
  isOpen: boolean;
  initialTab?: 'login' | 'register';
  onClose: () => void;
  onAuthSuccess: (isNewUser: boolean) => void;
}
export function AuthBottomSheet({
  isOpen,
  initialTab = 'login',
  onClose,
  onAuthSuccess
}: AuthBottomSheetProps) {
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [otpMethod, setOtpMethod] = useState<'whatsapp' | 'sms'>('whatsapp');
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [loginPhone, setLoginPhone] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [registerName, setRegisterName] = useState('');
  const [registerPhone, setRegisterPhone] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const otpInputs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    if (showOTP && resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
  }, [showOTP, resendTimer]);
  useEffect(() => {
    if (isOpen) setActiveTab(initialTab);
    if (!isOpen) {
      setShowOTP(false);
      setShowSuccess(false);
      setOtp(['', '', '', '', '', '']);
      setResendTimer(60);
      setCanResend(false);
    }
  }, [initialTab, isOpen]);
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      value = value.slice(-1);
    }
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      otpInputs.current[index + 1]?.focus();
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputs.current[index - 1]?.focus();
    }
  };
  const handleLogin = () => {
    setShowOTP(true);
    setResendTimer(60);
    setCanResend(false);
  };
  const handleRegister = () => {
    setShowOTP(true);
    setResendTimer(60);
    setCanResend(false);
  };
  const handleContinueWithOTP = () => {
    setShowOTP(true);
    setResendTimer(60);
    setCanResend(false);
  };
  const handleVerifyOTP = () => {
    setShowSuccess(true);
    setTimeout(() => {
      onAuthSuccess(activeTab === 'register');
    }, 2000);
  };
  const handleResendOTP = (method: 'whatsapp' | 'sms') => {
    setOtpMethod(method);
    setResendTimer(60);
    setCanResend(false);
    setOtp(['', '', '', '', '', '']);
    otpInputs.current[0]?.focus();
  };
  const handleEditPhone = () => {
    setShowOTP(false);
    setOtp(['', '', '', '', '', '']);
  };
  const currentPhone = activeTab === 'login' ? loginPhone : registerPhone;
  if (!isOpen) return null;
  if (showSuccess) {
    return <>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300" style={{
        backdropFilter: 'blur(4px)'
      }} data-id="element-2987" data-mp="1whhdaea2nxcg" />
        <div className="fixed bottom-0 left-0 right-0 z-[70] bg-white rounded-t-3xl shadow-2xl transition-transform duration-300 ease-out" style={{
        maxHeight: '90vh',
        height: 'auto',
        maxWidth: '640px',
        margin: '0 auto'
      }} data-id="element-2988" data-mp="25i5u1gquobt1">
          <div className="flex justify-center pt-3 pb-2" data-id="element-2989" data-mp="2t6yspkyuwxrc">
            <div className="w-12 h-1.5 bg-gray-300 rounded-full" data-id="element-2990" data-mp="2t5dzbyoa3kv2" />
          </div>
          <div className="px-6 py-12 text-center" data-id="element-2991" data-mp="2p7ga4vuicyro">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2992" data-mp="mjhr6tpcg3dm">
              <CheckCircleIcon className="w-12 h-12 text-green-600" data-id="element-2993" data-mp="rhj7x7zarqlx" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2994" data-mp="w8hpeaw0uuqr">
              {activeTab === 'register' ? 'Account Created!' : 'Welcome Back!'}
            </h2>
            <p className="text-gray-600" data-id="element-2995" data-mp="11a701pfc16eb">
              {activeTab === 'register' ? 'Your account has been successfully created' : 'You have successfully logged in'}
            </p>
          </div>
        </div>
      </>;
  }
  if (showOTP) {
    return <>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300" onClick={onClose} style={{
        backdropFilter: 'blur(4px)'
      }} data-id="element-2996" data-mp="dkmf50zmbz2e" />
        <div className="fixed bottom-0 left-0 right-0 z-[70] bg-white rounded-t-3xl shadow-2xl transition-transform duration-300 ease-out" style={{
        maxHeight: '90vh',
        height: 'auto',
        maxWidth: '640px',
        margin: '0 auto'
      }} data-id="element-2997" data-mp="1sz0fvm1tohnd">
          <div className="flex justify-center pt-3 pb-2" data-id="element-2998" data-mp="34oheb1p3qg3t">
            <div className="w-12 h-1.5 bg-gray-300 rounded-full" data-id="element-2999" data-mp="34mwjdayvkv5f" />
          </div>
          <div className="sticky top-0 bg-white px-6 pt-4 pb-4 rounded-t-3xl z-10" data-id="element-3000" data-mp="2zpejsb6qzsa3">
            <div className="flex items-center justify-between" data-id="element-3001" data-mp="2urmjotvy8zut">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-3002" data-mp="2ps080ovrrro4">
                Verify your mobile number
              </h2>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-3003" data-mp="2lr6ftjbanqpf">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-3004" data-mp="2gten4n2ts0k6" />
              </button>
            </div>
          </div>
          <div className="px-6 pb-6" data-id="element-3005" data-mp="ezynj0orfbd2">
            <p className="text-sm text-gray-600 text-center mb-2" data-id="element-3006" data-mp="f10bu6usducq">
              Enter the 6-digit OTP sent to
            </p>
            <div className="flex items-center justify-center gap-2 mb-4" data-id="element-3007" data-mp="y27ioy7yo049">
              <span className="text-base font-medium text-gray-900" data-id="element-3008" data-mp="12vtxavxoqe2p">
                +966 {currentPhone}
              </span>
              <button onClick={handleEditPhone} className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-3009" data-mp="p1j524mhc1rw">
                <EditIcon className="w-4 h-4 text-[#1E2B55]" data-id="element-3010" data-mp="1htcjbqizad6g" />
              </button>
            </div>
            <div className="flex items-center justify-center gap-1 mb-6" data-id="element-3011" data-mp="1lsv5sqqg6wbe">
              <span className="text-sm text-gray-600" data-id="element-3012" data-mp="17un0nvj9cop5">via</span>
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24" data-id="element-3013" data-mp="17w7ufixr03ly">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-3014" data-mp="3sjazrqs02zts" />
              </svg>
              <span className="text-sm font-medium text-green-600" data-id="element-3015" data-mp="2iki9nczp6ugi">
                WhatsApp
              </span>
            </div>
            <div className="flex justify-center gap-2 mb-6" data-id="element-3016" data-mp="221co95alufcz">
              {otp.map((digit, index) => <input key={index} ref={el => otpInputs.current[index] = el} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(index, e.target.value)} onKeyDown={e => handleOtpKeyDown(index, e)} className="w-12 h-14 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:border-[#1E2B55] focus:outline-none transition-colors" data-id="element-3017" data-mp="270xw657r5y1u" data-mpi={index} />)}
            </div>
            <div className="text-center mb-6" data-id="element-3018" data-mp="2zqxu1k3u6av7">
              <p className="text-sm text-gray-600 mb-3" data-id="element-3019" data-mp="2zo1yg2fgny9x">
                Didn't receive the OTP?
              </p>
              {canResend ? <div className="flex gap-3 justify-center" data-id="element-3020" data-mp="t2nr87e4vj6q">
                  <button onClick={() => handleResendOTP('whatsapp')} className="flex items-center gap-2 px-6 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors" data-id="element-3021" data-mp="o2j8sbto0edv">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" data-id="element-3022" data-mp="e5n82sa0cwjv">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-3023" data-mp="16sr78gmf6gnq" />
                    </svg>
                    WhatsApp
                  </button>
                  <button onClick={() => handleResendOTP('sms')} className="flex items-center gap-2 px-6 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors" data-id="element-3024" data-mp="ey9mryrljss">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-3025" data-mp="aa01hvkfczz2">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" data-id="element-3026" data-mp="jzp2c7k5pcod" />
                    </svg>
                    SMS
                  </button>
                </div> : <p className="text-sm text-gray-500" data-id="element-3027" data-mp="1a09ql3s28c6">
                  Resend OTP in{' '}
                  <span className="font-bold text-[#1E2B55]" data-id="element-3028" data-mp="678rwloxecdf">
                    {resendTimer}s
                  </span>
                </p>}
            </div>
            <button onClick={handleVerifyOTP} disabled={otp.some(digit => !digit)} className="w-full py-3 bg-[#1E2B55] text-white font-bold rounded-lg hover:bg-[#162041] transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-4" data-id="element-3029" data-mp="3sez1b6sr21rx">
              Confirm
            </button>
            <p className="text-xs text-gray-500 text-center" data-id="element-3030" data-mp="2zllrkansbu6f">
              By continuing, you agree to share your contact details with Innova
              for service purposes and accept their{' '}
              <a href="#" className="text-[#1E2B55] underline" data-id="element-3031" data-mp="3s86e8xtbfi3n">
                terms
              </a>{' '}
              and{' '}
              <a href="#" className="text-[#1E2B55] underline" data-id="element-3032" data-mp="3n8k4iycp4jzl">
                policies
              </a>
            </p>
          </div>
        </div>
      </>;
  }
  return <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300" onClick={onClose} style={{
      backdropFilter: 'blur(4px)'
    }} data-id="element-3033" data-mp="3dbyufyltkiow" />
      <div className="fixed bottom-0 left-0 right-0 z-[70] bg-white rounded-t-3xl shadow-2xl transition-transform duration-300 ease-out" style={{
      maxHeight: '90vh',
      height: 'auto',
      maxWidth: '640px',
      margin: '0 auto',
      transform: isOpen ? 'translateY(0)' : 'translateY(100%)'
    }} data-id="element-3034" data-mp="21z65swobsad2">
        <div className="flex justify-center pt-3 pb-2" data-id="element-3035" data-mp="w4p6j0yy88zr">
          <div className="w-12 h-1.5 bg-gray-300 rounded-full" data-id="element-3036" data-mp="1130d92ty63dr" />
        </div>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 pt-4 pb-0 rounded-t-3xl z-10" data-id="element-3037" data-mp="11536rwub6en8">
          <div className="flex items-center justify-between mb-4" data-id="element-3038" data-mp="156gbu4pkudt3">
            <h2 className="text-lg font-bold text-gray-900" data-id="element-3039" data-mp="1a480mpuxdjt2">
              {activeTab === 'login' ? 'Sign In' : 'Create Account'}
            </h2>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-3040" data-mp="3bvberp40xgid">
              <XIcon className="w-5 h-5 text-gray-500" data-id="element-3041" data-mp="3guoaz0gsbzxm" />
            </button>
          </div>
          <div className="flex bg-gray-100 rounded-lg p-1" data-id="element-3042" data-mp="1b4kl4755hz92">
            <button onClick={() => setActiveTab('login')} className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${activeTab === 'login' ? 'bg-white text-[#1E2B55] shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-3043" data-mp="1671798cd7ohn">
              Login
            </button>
            <button onClick={() => setActiveTab('register')} className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${activeTab === 'register' ? 'bg-white text-[#1E2B55] shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-3044" data-mp="seuqbk1e1dh5">
              Create Account
            </button>
          </div>
        </div>
        <div className="overflow-y-auto px-6 py-6" style={{
        maxHeight: 'calc(90vh - 240px)'
      }} data-id="element-3045" data-mp="wev6b944xwqb">
          {activeTab === 'login' ? <div className="space-y-4" data-id="element-3046" data-mp="4pjp61g4lh2h">
              <div data-id="element-3047" data-mp="3qrixhd32ktp7">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3048" data-mp="3qqzdlqtuzv8j">
                  Phone Number
                </label>
                <div className="flex" data-id="element-3049" data-mp="3vjaf2y48zp37">
                  <div className="flex items-center px-3 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg" data-id="element-3050" data-mp="3gtblz9rjv3ul">
                    <span className="text-sm text-gray-700" data-id="element-3051" data-mp="3lrd49sh5iz50">+966</span>
                  </div>
                  <input type="tel" value={loginPhone} onChange={e => setLoginPhone(e.target.value)} placeholder="5xxxxxxxx" className="flex-1 px-4 py-3 border border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-3052" data-mp="dbtamsckjepf" />
                </div>
              </div>
              <div data-id="element-3053" data-mp="2o2ul39e7mjww">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3054" data-mp="2o6ifearhbaco">
                  Password
                </label>
                <div className="relative" data-id="element-3055" data-mp="zkiae0gp1gtn">
                  <input type={showPassword ? 'text' : 'password'} value={loginPassword} onChange={e => setLoginPassword(e.target.value)} placeholder="Enter your password" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent pr-12" data-id="element-3056" data-mp="zk8k0brql7nf" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700" data-id="element-3057" data-mp="bv75q7w832a8">
                    {showPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-3058" data-mp="21a3ywc4249oq" /> : <EyeIcon className="w-5 h-5" data-id="element-3059" data-mp="217rhq164i3cq" />}
                  </button>
                </div>
              </div>
              <div className="text-right" data-id="element-3060" data-mp="23vbo1h3ei30q">
                <button className="text-sm text-[#1E2B55] hover:underline font-medium" data-id="element-3061" data-mp="23ua2giordy4r">
                  Forgot password?
                </button>
              </div>
              <div className="relative" data-id="element-3062" data-mp="2dleepan1v4vw">
                <div className="absolute inset-0 flex items-center" data-id="element-3063" data-mp="2dmfzi6tvb5qt">
                  <div className="w-full border-t border-gray-200" data-id="element-3064" data-mp="2iiwpl6kmnu6j" />
                </div>
                <div className="relative flex justify-center text-sm" data-id="element-3065" data-mp="1kwzcxpsxij2i">
                  <span className="px-2 bg-white text-gray-500" data-id="element-3066" data-mp="1pu90ezwrcknp">Or</span>
                </div>
              </div>
              <button onClick={handleContinueWithOTP} className="w-full py-3 px-4 border-2 border-[#1E2B55] text-[#1E2B55] font-medium rounded-lg hover:bg-blue-50 transition-colors" data-id="element-3067" data-mp="1utltw3i4wpz9">
                Continue with OTP
              </button>
            </div> : <div className="space-y-4" data-id="element-3068" data-mp="3b6sn2di0tq4d">
              <div data-id="element-3069" data-mp="6rqjd9vr06o">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3070" data-mp="68j4qhuvu6w">
                  Full Name
                </label>
                <input type="text" value={registerName} onChange={e => setRegisterName(e.target.value)} placeholder="Enter your full name" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-3071" data-mp="3redyhi3mtotc" />
              </div>
              <div data-id="element-3072" data-mp="ev4nodkpep2r">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3073" data-mp="exqzwqijhsrb">
                  Phone Number
                </label>
                <div className="flex" data-id="element-3074" data-mp="nvuknklpali2">
                  <div className="flex items-center px-3 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg" data-id="element-3075" data-mp="nwvtrjj5oxx5">
                    <span className="text-sm text-gray-700" data-id="element-3076" data-mp="2uowoiqxm9glu">+966</span>
                  </div>
                  <input type="tel" value={registerPhone} onChange={e => setRegisterPhone(e.target.value)} placeholder="5xxxxxxxx" className="flex-1 px-4 py-3 border border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-3077" data-mp="2pptt7y7fuor2" />
                </div>
              </div>
              <div data-id="element-3078" data-mp="mfgruptnlad0">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3079" data-mp="me5m4kgndsj9">
                  Email <span className="text-gray-400" data-id="element-3080" data-mp="rejvq6xwdaje">(Optional)</span>
                </label>
                <input type="email" value={registerEmail} onChange={e => setRegisterEmail(e.target.value)} placeholder="your@email.com" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-3081" data-mp="chj2fz0257it" />
              </div>
              <div data-id="element-3082" data-mp="3fdrtlqekxbys">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3083" data-mp="3fdhyxpilagmr">
                  Password
                </label>
                <div className="relative" data-id="element-3084" data-mp="3k74d5m4eisko">
                  <input type={showPassword ? 'text' : 'password'} value={registerPassword} onChange={e => setRegisterPassword(e.target.value)} placeholder="Create a password" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent pr-12" data-id="element-3085" data-mp="36e4wvhwsouc2" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700" data-id="element-3086" data-mp="3p3uusdvutw87">
                    {showPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-3087" data-mp="2ilxw86k1e16u" /> : <EyeIcon className="w-5 h-5" data-id="element-3088" data-mp="2b2yfy9rvgprp" />}
                  </button>
                </div>
              </div>
              <div className="flex items-start" data-id="element-3089" data-mp="2k57m6ng3xwza">
                <button onClick={() => setAcceptTerms(!acceptTerms)} className={`flex-shrink-0 w-5 h-5 flex items-center justify-center border-2 rounded ${acceptTerms ? 'bg-[#1E2B55] border-[#1E2B55]' : 'border-gray-300'}`} data-id="element-3090" data-mp="2k3wn30xlxdei">
                  {acceptTerms && <CheckIcon className="w-3 h-3 text-white" data-id="element-3091" data-mp="1ng8dx8fiwv73" />}
                </button>
                <label className="ml-3 text-sm text-gray-600" data-id="element-3092" data-mp="217dj0q1nufot">
                  I agree to the{' '}
                  <a href="#" className="text-[#1E2B55] hover:underline font-medium" data-id="element-3093" data-mp="1wekdw12a55c7">
                    Terms & Conditions
                  </a>{' '}
                  and{' '}
                  <a href="#" className="text-[#1E2B55] hover:underline font-medium" data-id="element-3094" data-mp="13ovchmlnr3a2">
                    Privacy Policy
                  </a>
                </label>
              </div>
            </div>}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4" data-id="element-3095" data-mp="3djing0ruk8e4">
          <button onClick={activeTab === 'login' ? handleLogin : handleRegister} disabled={activeTab === 'register' && !acceptTerms} className="w-full py-3 px-6 bg-[#1E2B55] text-white font-bold rounded-lg hover:bg-[#162041] transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3096" data-mp="3igsefkuvws42">
            {activeTab === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </div>
      </div>
    </>;
}